import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
import { getImageUrl } from "../../utils/image";

const aboutSubmenu = [
  { label: "About Us", path: "/about", icon: "Info" },
  { label: "Our Services", path: "/services", icon: "Wrench" },
  { label: "Gallery", path: "/gallery", icon: "Image" },
];

const navLinks = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about", hasDropdown: true, submenu: aboutSubmenu },
  { label: "Products", path: "/products" },
  { label: "Blog", path: "/blog" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopDropOpen, setDesktopDropOpen] = useState(false);
  const [mobileDropOpen, setMobileDropOpen] = useState(false);
  const dropRef = useRef(null);
  const location = useLocation();
  const { get } = useSettings();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDesktopDropOpen(false);
    setMobileDropOpen(false);
  }, [location]);

  useEffect(() => {
    const fn = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDesktopDropOpen(false);
      }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const isActive = (path) => path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  // Dynamic menu list parsing
  let linksList = navLinks;
  const rawMenu = get("navigation_menu_json");
  if (rawMenu) {
    try {
      linksList = JSON.parse(rawMenu);
    } catch (e) {
      console.error("Invalid navigation_menu_json", e);
    }
  }

  const logoUrl = getImageUrl(get("site_logo") || "/logo.png");
  const headerCtaText = get("header_cta_text") || "Get in Touch";
  const headerCtaLink = get("header_cta_link") || "/contact";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-soft border-b border-gray-100 transition-all duration-300">
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 md:h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-0">
            <img src={logoUrl} alt={get("site_name") || "Sava Fresh"} className="h-10 md:h-12 w-auto object-contain" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {linksList.map((link) =>
              link.hasDropdown ? (
                <div key={link.path} className="relative" ref={dropRef}>
                  <button
                    onClick={() => setDesktopDropOpen(!desktopDropOpen)}
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive(link.path) ? "text-brand-primary bg-brand-pale" : "text-gray-600 hover:text-brand-primary hover:bg-gray-50"}`}
                  >
                    {link.label}
                    <LucideIcons.ChevronDown className={`w-3.5 h-3.5 transition-transform ${desktopDropOpen ? "rotate-180" : ""}`} />
                  </button>
                  {desktopDropOpen && (
                    <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-2xl shadow-hover border border-gray-100 overflow-hidden z-50">
                      <div className="p-2">
                        {link.submenu?.map((sub) => {
                          const IconComponent = LucideIcons[sub.icon] || LucideIcons.HelpCircle;
                          return (
                            <Link key={sub.path} to={sub.path} onClick={() => setDesktopDropOpen(false)}
                              className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-brand-pale transition-colors group">
                              <div className="w-8 h-8 bg-brand-pale rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-brand-muted transition-colors">
                                <IconComponent className="w-4 h-4 text-brand-primary" />
                              </div>
                              <span className="text-sm font-medium text-gray-700 group-hover:text-brand-primary">{sub.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link key={link.path} to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isActive(link.path) ? "text-brand-primary bg-brand-pale" : "text-gray-600 hover:text-brand-primary hover:bg-gray-50"}`}>
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${get("call_number")}`} className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-brand-primary transition-colors">
              <LucideIcons.Phone className="w-4 h-4" />
              <span className="hidden xl:block">{get("call_number")}</span>
            </a>
            <Link to={headerCtaLink} className="btn-primary text-xs px-5 py-2.5">{headerCtaText}</Link>
          </div>

          {/* Mobile Toggle */}
          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors">
            {menuOpen ? <LucideIcons.X className="w-5 h-5 text-gray-700" /> : <LucideIcons.Menu className="w-5 h-5 text-gray-700" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden transition-all duration-300 overflow-hidden ${menuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="bg-white border-t border-gray-100 px-4 py-4 shadow-soft">
          <nav className="flex flex-col gap-1">
            {linksList.map((link) => (
              link.hasDropdown ? (
                <div key={link.path}>
                  <button onClick={() => setMobileDropOpen(!mobileDropOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium text-gray-600 hover:bg-brand-pale hover:text-brand-primary transition-all">
                    {link.label} <LucideIcons.ChevronDown className={`w-4 h-4 transition-transform ${mobileDropOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileDropOpen && <div className="ml-4 mt-1 space-y-1">
                    {link.submenu?.map((sub) => {
                      const IconComponent = LucideIcons[sub.icon] || LucideIcons.HelpCircle;
                      return (
                        <Link key={sub.path} to={sub.path}
                          onClick={() => { setMenuOpen(false); setMobileDropOpen(false); }}
                          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm text-gray-600 hover:bg-brand-pale hover:text-brand-primary transition-all">
                          <IconComponent className="w-4 h-4 text-brand-primary" />{sub.label}
                        </Link>
                      );
                    })}
                  </div>}
                </div>
              ) : (
                <Link key={link.path} to={link.path}
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${isActive(link.path) ? "text-brand-primary bg-brand-pale" : "text-gray-600 hover:bg-brand-pale hover:text-brand-primary"}`}>
                  {link.label}
                </Link>
              )
            ))}
            <Link to={headerCtaLink} onClick={() => setMenuOpen(false)} className="btn-primary mt-3 justify-center">{headerCtaText}</Link>
          </nav>
        </div>
      </div>
    </header>
  );
}
